import { Component, createEffect, createSignal, onCleanup } from 'solid-js';
import { computePosition, shift } from '@floating-ui/dom';
import ContextMenu from './ContextMenu';
import type {
  ContextMenuItemConfig,
  ContextMenuItemMetaData,
} from './context-menu.types';
import type { AnchoredContextMenuProps } from './AnchoredContextMenu.types';

export * from './AnchoredContextMenu.types';

const LIST_ITEM_PADDING = 0.5; // in rem, needs to be in sync with styling from figma
const REM_WIDTH_IN_PX = 16; // 1rem = 16px

export const AnchoredContextMenu: Component<AnchoredContextMenuProps> = (
  props
) => {
  let anchorRef!: HTMLDivElement;
  let menuRef: HTMLDivElement;

  const [showContextMenu, setShowContextMenu] = createSignal(false);

  const setMenuOpen = (isOpen: boolean): void => {
    setShowContextMenu(isOpen);
    props.onOpenChange?.(isOpen);
  };

  const contextMenuItems = (): ContextMenuItemConfig[] =>
    props.menuItems.map((item) => ({
      label: item.label,
      reference: item.value,
      disabled: item.disabled ?? false,
      accessConfig: item.accessConfig,
      disabledReasonTooltip: item.disabledReasonTooltip,
      handleMenuAction: (
        _: Record<string, unknown>,
        metadata: ContextMenuItemMetaData
      ) => {
        props.onSelection?.({
          event: metadata.event,
          value: props.value,
          menuItem: metadata.value,
        });
        metadata.event.stopImmediatePropagation();
      },
    }));

  createEffect(() => {
    if (showContextMenu()) {
      const tabAligned = props.tabAligned ?? false;
      void computePosition(anchorRef, menuRef!, {
        placement: props.menuPlacement ?? 'bottom',
        middleware: [shift()],
      }).then(({ x, y }) => {
        let padding = 0;
        const offsetX =
          menuRef!.clientWidth / 2 - // eslint-disable-line no-magic-numbers
          anchorRef.clientWidth / 2 - // eslint-disable-line no-magic-numbers
          LIST_ITEM_PADDING * REM_WIDTH_IN_PX +
          1; //account for the 1px border of the menu.
        if (tabAligned) x = x - offsetX;

        const requiredSpace = menuRef!.clientHeight + anchorRef.clientHeight;
        const bottomPx = anchorRef.getBoundingClientRect().top + requiredSpace;

        if (bottomPx > window.innerHeight) {
          padding = -requiredSpace;
        }

        Object.assign(menuRef!.style, {
          left: `${x.toString()}px`,
          top: `${(y + padding).toString()}px`,
        });
      });
    }
  });

  createEffect(() => {
    if (!showContextMenu()) {
      return;
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    onCleanup(() => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    });
  });

  const handleClickOutside = (event: MouseEvent): void => {
    // The event target when clicking on the anchor after the menu is open
    // evaluates to the trb-civil-3d-viewer element. So instead, we need to check whether
    // the XY of the click is inside the bounding rectangel of the anchor.
    const anchorRect = anchorRef.getBoundingClientRect();
    const isClickInsideAnchor =
      event.clientX >= anchorRect.left &&
      event.clientX <= anchorRect.right &&
      event.clientY >= anchorRect.top &&
      event.clientY <= anchorRect.bottom;

    if (
      // If the event target is inside the portal container
      menuRef!.contains(event.target as Node) ||
      // Or the portal container is the event target
      (event.target as HTMLElement).shadowRoot?.contains(menuRef!) ||
      isClickInsideAnchor
    ) {
      return;
    }
    setMenuOpen(false);
  };

  const handleVisibilityChange = (): void => {
    if (document.hidden) {
      setMenuOpen(false);
    }
  };

  return (
    <div data-testid={props.name ? `iconcontextmenu-${props.name}` : null}>
      <div
        ref={anchorRef}
        class="inline-flex items-center"
        onClick={(e) => {
          setMenuOpen(!showContextMenu());
          e.stopImmediatePropagation();
        }}
      >
        {props.children}
      </div>
      <ContextMenu
        ref={menuRef!}
        show={showContextMenu()}
        onCloseMenu={() => setMenuOpen(false)}
        contextMenuItems={contextMenuItems()}
        dataItem={{ value: props.value }}
      />
    </div>
  );
};

export default AnchoredContextMenu;
